/**
 * Consistent error handling for API/async operations.
 * Reduces repeated try/catch patterns across the app.
 */

/** API field key -> human-readable label (for merchant/DBA forms) */
const MERCHANT_FIELD_LABELS = {
  dba_name: 'DBA Name',
  ssn: 'SSN Number',
  lead_id: 'Lead',
  dba_email: 'DBA Email',
  dba_phone: 'DBA Phone',
  dba_address_street: 'DBA Address',
  dba_address_city: 'DBA City',
  dba_address_state: 'DBA State',
  dba_address_zip_code: 'DBA Zip Code',
  'dba_address.street': 'DBA Address',
  'dba_address.city': 'DBA City',
  'dba_address.state': 'DBA State',
  'dba_address.zip_code': 'DBA Zip Code',
  merchant_federal_tax: 'Federal Tax ID/EIN',
  tax_id: 'Federal Tax ID/EIN',
  banking_bank_name: 'Bank Name',
  banking_routing_number: 'Routing Number',
  banking_account_number: 'Account Number',
  banking_confirm_account_number: 'Confirm Account Number',
  banking_account_type: 'Account Type',
  banking_payment_methods_sum: 'Payment Methods (must sum to 100%)',
  banking_present_transaction: 'Card Present Transaction %',
  banking_moto_transaction: 'Card MOTO Transaction %',
  banking_ecom_transaction: 'Internet %',
  principals: 'Principals (at least one owner required)',
  'principals.0': 'Principal',
  principals_0: 'Principal',
  // Upload / attachment fields
  file: 'File',
  model_id: 'Model ID (merchant/DBA must be saved first)',
  model_type: 'Model type',
};

/**
 * Converts API field key to human-readable label.
 * Handles nested keys like principals.0.first_name -> Principal first name.
 */
const getFieldLabel = (key) => {
  if (MERCHANT_FIELD_LABELS[key]) return MERCHANT_FIELD_LABELS[key];
  const base = key.split('.')[0] || key.split('_')[0];
  if (MERCHANT_FIELD_LABELS[base]) return MERCHANT_FIELD_LABELS[base];
  return key.replace(/_/g, ' ').replace(/\.\d+\.?/g, ' ');
};

/** Maps API keys (e.g. dba_address.street) to form field keys (dba_address_street) */
const API_KEY_TO_FORM = {
  'dba_address.street': 'dba_address_street',
  'dba_address.city': 'dba_address_city',
  'dba_address.state': 'dba_address_state',
  'dba_address.zip_code': 'dba_address_zip_code',
  'banking.bank_name': 'banking_bank_name',
  'banking.routing_number': 'banking_routing_number',
  'banking.account_number': 'banking_account_number',
  'banking.account_type': 'banking_account_type',
  'principals.0': 'principals',
};

const toFormKey = (key) => {
  if (API_KEY_TO_FORM[key]) return API_KEY_TO_FORM[key];
  if (key.startsWith('principals.')) return 'principals';
  return key.replace(/\./g, '_');
};

/**
 * Formats API validation errors into a user-friendly message.
 * @param {Object} errors - API errors object, e.g. { field: ["message"], principals: ["At least one required"] }
 * @returns {{ message: string, errors: Object }} - Toast message and normalized errors for form state
 */
const normalizeErrorMessages = (msgs) => {
  if (Array.isArray(msgs)) return msgs.filter(Boolean);
  if (typeof msgs === 'string' && msgs.trim()) return [msgs];
  if (msgs != null && typeof msgs !== 'object') return [String(msgs)];
  return [];
};

export const formatApiValidationErrors = (errors) => {
  if (!errors || typeof errors !== 'object') return { message: 'Validation failed', errors: {} };
  const entries = Object.entries(errors)
    .map(([key, msgs]) => [key, normalizeErrorMessages(msgs)])
    .filter(([, msgs]) => msgs.length > 0);
  if (entries.length === 0) return { message: 'Validation failed', errors: {} };
  const parts = entries.map(([key, msgs]) => {
    const label = getFieldLabel(key);
    const msg = msgs[0] || 'is required';
    return `${label}: ${msg}`;
  });
  const formErrors = Object.fromEntries(
    entries.map(([k, v]) => [toFormKey(k), v[0]])
  );
  return {
    message: parts.join('. '),
    errors: formErrors,
  };
};

/** A field -> messages bag, e.g. { email: ["The email is invalid"] } */
const looksLikeErrorBag = (value) =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0 &&
  Object.values(value).every(
    (msgs) => Array.isArray(msgs) || typeof msgs === 'string'
  );

/**
 * Finds the field-error bag on any supported error shape.
 * The API wraps failures as { success: false, status_code, message, data: { field: [msg] } },
 * so validation errors arrive under `data` — not under `errors`.
 */
export const extractApiErrorBag = (error) => {
  const body = error?.response?.data ?? error?.data ?? error;
  const candidates = [
    body?.errors,
    error?.errors,
    body?.success === false ? body?.data : undefined,
  ];
  return candidates.find((bag) => looksLikeErrorBag(bag)) || null;
};

/**
 * Extracts a user-friendly message from an error object.
 * Supports axios errors, RTK Query unwrap errors, and plain payloads.
 * @param {Error|Object} error - Error or API payload (e.g. from rejectWithValue)
 * @returns {string}
 */
export const getErrorMessage = (error) => {
  const validationErrors = extractApiErrorBag(error);
  if (validationErrors && typeof validationErrors === 'object') {
    const { message } = formatApiValidationErrors(validationErrors);
    if (message && message !== 'Validation failed') return message;
  }
  return (
    error?.response?.data?.message ||
    error?.data?.message ||
    (typeof error?.data === 'string' ? error.data : null) ||
    error?.apiMessage ||
    error?.message ||
    (typeof error === 'string' ? error : null) ||
    'Something went wrong. Please try again.'
  );
};

/**
 * Gets user-friendly message from API validation error (thunk / RTK unwrap).
 * Prefers field-level errors when present.
 * @param {string|Object} error - Rejected value from unwrap() (string or { message, errors })
 * @returns {{ message: string, errors: Object }}
 */
export const getValidationErrorDetails = (error) => {
  if (typeof error === 'string') return { message: error, errors: {} };
  const errs = extractApiErrorBag(error);
  if (errs && typeof errs === 'object' && Object.keys(errs).length > 0) {
    const { message, errors } = formatApiValidationErrors(errs);
    return { message, errors };
  }
  return {
    message:
      error?.data?.message ||
      error?.response?.data?.message ||
      error?.message ||
      'Failed to save. Please try again.',
    errors: {},
  };
};

/**
 * Parses upload error response (e.g. from Resumable.js fileError - xhr.responseText).
 * Extracts validation/error message from 422 or other API error responses.
 * @param {string} responseText - Raw response body (often JSON string)
 * @param {string} fileName - Optional file name for context
 * @returns {string} User-friendly error message for toast
 */
export const parseUploadErrorResponse = (responseText, fileName = '') => {
  if (!responseText || typeof responseText !== 'string') {
    return fileName ? `Upload failed for ${fileName}. Please try again.` : 'Upload failed. Please try again.';
  }
  const trimmed = responseText.trim();
  if (!trimmed) {
    return fileName ? `Upload failed for ${fileName}. Server returned an empty response.` : 'Upload failed. Server returned an empty response.';
  }
  try {
    const data = JSON.parse(trimmed);
    const msg = data?.message;
    const errs = data?.errors;
    if (errs && typeof errs === 'object' && Object.keys(errs).length > 0) {
      const { message } = formatApiValidationErrors(errs);
      return fileName ? `${fileName}: ${message}` : message;
    }
    if (msg && typeof msg === 'string') {
      return fileName ? `${fileName}: ${msg}` : msg;
    }
  } catch {
    // Not JSON - use as-is if it looks like a message, else generic
    if (trimmed.length < 200) return fileName ? `${fileName}: ${trimmed}` : trimmed;
  }
  return fileName ? `Upload failed for ${fileName}. Please check file type and size.` : 'Upload failed. Please check file type and size.';
};
